<script setup>
import { Head, Link } from '@inertiajs/inertia-vue3';
import LandingPageLayout from '@/Layouts/LandingPageLayout.vue';
import {
    ArrowPathIcon,
    InboxIcon,
    CloudArrowUpIcon,
    CogIcon,
    LockClosedIcon,
    ServerIcon,
    ShieldCheckIcon,
    SparklesIcon,
    UserGroupIcon,
    CheckIcon
} from '@heroicons/vue/24/outline'

const features = [
    {
        name: 'Digital Maintenance Records',
        description: 'Easy to follow forms help you generate complete and accurate maintenance records. Electronically sign them or substantiate with scans of historical paper records. Utilize powerful OCR to generate work descriptions, or our professional team to <strong><em>hand type</em></strong> (we are the only company in the industry to offer this) for unprecedented accuracy.',
        icon: CheckIcon,
        show: true,
    },
    {
        name: 'Maintenance Tracking',
        description: 'Track everything you need from inspections, lubrication intervals, SBs, ADs, and much more. Customize groups of inspections to comprise a larger event and receive email alerting with customizable frequencies and pausing. Utilize our intelligently recommended ADs and Overhaul Schedules unique to your aircraft, engine(s), and propeller(s).',
        show: true,
    },
    {
        name: 'Equipment Lists',
        description: 'Create your bespoke equipment list which can help you track the components most important to your operation.',
        show: true,
    },
    {
        name: 'Squawks',
        description: 'Create and track squawks which you can share with anyone. Message back and forth with stakeholders, upload images, video, and link the squawk to a maintenance record to close it out while maintaining a trail of how you fixed it.',
        show: true,
    },
    {
        name: 'Listings',
        description: 'Instantly list your plane for sale on our platform, allowing for secure read-only access for anyone wishing to research your airplane. They will be amazed with the way your logbooks are presented.',
        show: true,
    },
    {
        name: 'Flight Log',
        description: 'Keep track of all your flights and optionally record metadata including fuel consumption, fuel price, oil added, to and from destinations, mission descriptions, and more.',
        show: true,
    },
    {
        name: 'FlightAware',
        description: 'Automatic integration with FlightAware to keep your flight log up to date with minimal effort. Receive email alerts upon landing to adjust the time as needed.',
        show: true,
    },
    {
        name: 'In-House Scanning with Secure Shipping',
        description: 'We are the only company in General Aviation willing to scan all of your maintenance records in-house, using full time PLANELOGIX employees trained specifically in working with your delicate and precious logbooks. We never contract this work out to anyone, and your records never leave our possession until it is time to return them to you. This ensures you are receiving the highest level of quality control and standards that only we can provide. We utilize secure, waterproof, locked, and Apple AirTag tracked Pelican case shipments to facilitate moving logbooks all around the country.',
        show: true,
    },
    {
        name: 'KRDU, KBUY, and KTTA Pick Ups',
        description: 'While we take extreme precautions with shipping, we are sensitive to the hesitation some customers have. After all, we exist because our logbooks went missing momentarily. Therefore, we are happy to meet you at any of these airports for a nominal fee.',
        show: true,
    },
    {
        name: 'Executive Binder',
        description: 'Another PLANELOGIX first, and unique only to us, we can provide you with hardcopy representation of your critical documents so that you can get work done on the airplane without the need for your originals or a computer.',
        show: true,
    },
    {
        name: 'FAA Compliant and Secure',
        description: 'Our platform is designed in accordance with AC 120-78B and secured with enterprise grade protocols.',
        show: true,
    },
    {
        name: 'Your Records',
        description: 'All of your documents and records which are stored in digital form on our platform are always available to you for export or download at any time and are fully transferable to the next aircraft owner, just like your paper records.',
        show: true,
    },
    {
        name: 'Scheduler',
        description: 'Use our powerful scheduler to keep track of everything related to your airplane including shop time, flights, and more.',
        show: false,
    },
    {
        name: 'Fleet View',
        description: 'Our fleet view makes managing more than one airplane incredibly easy and straightforward.',
        show: false,
    },
]

</script>

<template>
    <LandingPageLayout title="Product">
        <div class="isolate overflow-hidden">
            <div class="flow-root bg-gray-900 py-16 sm:pt-32 lg:pb-30">
                <div class="mx-auto max-w-7xl px-6 lg:px-8">
                    <div class="relative z-10">
                        <h1 class="mx-auto max-w-4xl text-center text-5xl font-bold tracking-tight text-white">Our Product and Services</h1>
                        <p class="mx-auto mt-4 max-w-2xl text-center text-lg leading-8 text-white/60">Learn more about what we offer</p>
                    </div>
                    <div class="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:-mb-14 lg:max-w-none lg:grid-cols-2">
                        <svg viewBox="0 0 1208 1024" aria-hidden="true" class="absolute -bottom-48 left-1/2 h-[64rem] -translate-x-1/2 translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] lg:-top-48 lg:bottom-auto lg:translate-y-0">
                            <ellipse cx="604" cy="512" fill="url(#d25c25d4-6d43-4bf9-b9ac-1842a30a4867)" rx="604" ry="512" />
                            <defs>
                                <radialGradient id="d25c25d4-6d43-4bf9-b9ac-1842a30a4867">
                                    <stop stop-color="#111827" />
                                    <stop offset="1" stop-color="#01b2ff" />
                                </radialGradient>
                            </defs>
                        </svg>
                        <div class="hidden lg:absolute lg:inset-x-px lg:bottom-0 lg:top-4 lg:block lg:rounded-t-2xl lg:bg-gray-800/80 lg:ring-1 lg:ring-white/10" aria-hidden="true" />
                    </div>
                </div>
            </div>
        </div>
        <!-- Digital only or Digital Hardcopy -->
        <div class="mx-auto max-w-2xl px-4 py-24 sm:px-6 sm:py-16 lg:max-w-7xl lg:px-8">
            <!-- Details section -->
            <section aria-labelledby="details-heading">
                <div class="flex flex-col items-center text-center">
                    <p class="mt-3 max-w-3xl text-lg text-gray-600">At it's core, PLANELOGIX is a document processing and digitization platform specializing in aircraft records, and only aircraft records. We take pride in our specialization and leverage it to offer you the best products and services available in General Aviation.</p>

                    <p class="mt-3 max-w-3xl text-lg text-gray-600">Our process starts with your scans, either client provided or PLANELOGIX generated, and concludes with various digitization upgrades you can stack on top of those scans. You can provide us with the scans, have us ship you a Pelican Case shipping container for a secure shipment, or fly to us at KRDU, KBUY, or KTTA.</p>

                    <p class="mt-3 max-w-3xl text-lg text-gray-600">From there, we offer both digital only and digital + hardcopy service options to provide you with a seamless aircraft management experience... the best of both worlds!</p>
                </div>

                <!-- digital and hardcopy + digital -->
                <div class="mt-16 grid grid-cols-1 gap-y-16 lg:grid-cols-2 lg:gap-x-8">
                    <div>
                        <h3 class="text-3xl text-center font-bold text-gray-900 sm:text-3xl pb-3">Digital Only</h3>
                        <div class="aspect-w-3 aspect-h-2 w-full overflow-hidden rounded-lg bg-logix-dark-blue-900">
                            <img src="https://logixpublic.s3.amazonaws.com/partnerships/Flyer2-01.PNG" alt="Drawstring top with elastic loop closure and textured interior padding." class="h-full w-full object-cover object-center" />
                        </div>
                        <p class="mt-8 text-base text-gray-500">Our app is <a class="font-medium text-indigo-600 hover:text-indigo-500" href="https://www.faa.gov/regulations_policies/advisory_circulars/index.cfm/go/document.information/documentID/1043396" target="_blank">FAA AC 120-78B</a> compliant, allowing you to optionally use electronic signatures for new entries. Or upload images of the signed paper!</p>
                    </div>
                    <div>
                        <h3 class="text-3xl text-center font-bold text-gray-900 sm:text-3xl pb-3">Digital + Hardcopy</h3>
                        <div class="aspect-w-3 aspect-h-2 w-full overflow-hidden rounded-lg">
                            <img src="https://logixpublic.s3.amazonaws.com/images/execBinder.png" alt="Executive Binder." class="h-full w-full object-cover object-center" />
                        </div>
                        <p class="mt-8 text-base text-gray-500">We are the only providers offering a complete bridge from paper to digital. Offered as a separate and paid add-on, you can utilize our Executive Binder to carry with you a fully functional maintenance logbook with photocopies of your critical scans, custom tabular organization, custom metal nameplate, plastic reinforced pages, and templates for new entries. Here's a <a class="underline text-gray-900 dark:text-white" href="https://www.youtube.com/embed/LHQvNFVU5q8" target="_blank">video</a> of the binder.</p>
                    </div>
                </div>

                <!-- dashboard preview -->
                <div class="relative overflow-hidden bg-white pt-16 pb-32">
                    <div class="relative">
                        <div class="lg:mx-auto lg:grid lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24 lg:px-8">
                            <div class="mx-auto max-w-xl px-6 lg:mx-0 lg:max-w-none lg:py-16 lg:px-0">
                                <div>
                                    <div>
                                      <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-logix-light-blue">
                                        <InboxIcon class="h-8 w-8 text-white" aria-hidden="true" />
                                      </span>
                                    </div>
                                    <div class="mt-6">
                                        <h2 class="text-3xl font-bold tracking-tight text-gray-900">Stay Ahead of Your Aircraft</h2>
                                        <p class="mt-4 text-lg text-gray-500">Our maintenance tracking tools make understanding the complete picture for your aircraft easy. And with automatic updates from FlightAware, the job is even easier.</p>
                                        <div class="mt-6">
<!--                                            <a href="/sign-up" class="inline-flex rounded-lg bg-logix-light-blue px-4 py-1.5 text-base font-semibold leading-7 text-white shadow-sm ring-1 ring-indigo-600 hover:bg-logix-dark-blue hover:ring-logix-light-blue-700">Get started</a>-->
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-8 border-t border-gray-200 pt-6">
                                    <blockquote>
                                        <div>
                                            <p class="text-base text-gray-500">&ldquo;I bought your service last year and love it. Best thing I ever did.&rdquo;</p>
                                        </div>
                                        <footer class="mt-3">
                                            <div class="flex items-center space-x-3">
                                                <div class="flex-shrink-0">
                                                    <img class="h-6 w-6 rounded-full" src="https://logixpublic.s3.amazonaws.com/partnerships/sok.jpg" alt="" />
                                                </div>
                                                <div class="text-base font-medium text-gray-700">Jim Sok, Beechcraft F33A Owner</div>
                                            </div>
                                        </footer>
                                    </blockquote>
                                </div>
                            </div>
                            <div class="mt-12 sm:mt-16 lg:mt-0">
                                <div class="-mr-48 pl-4 sm:pl-6 md:-mr-16 lg:relative lg:m-0 lg:h-full lg:px-0">
                                    <img class="w-full rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 lg:absolute lg:left-0 lg:h-full lg:w-auto lg:max-w-none" src="https://logixpublic.s3.amazonaws.com/features/trxitems.PNG" alt="Inbox user interface" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-24">
                        <div class="lg:mx-auto lg:grid lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24 lg:px-8">
                            <div class="mx-auto max-w-xl px-6 lg:col-start-2 lg:mx-0 lg:max-w-none lg:py-32 lg:px-0">
                                <div>
                                    <div>
                                      <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-logix-light-blue">
                                        <UserGroupIcon class="h-8 w-8 text-white" aria-hidden="true" />
                                      </span>
                                    </div>
                                    <div class="mt-6">
                                        <h2 class="text-3xl font-bold tracking-tight text-gray-900">Communicate With Your Stakeholders</h2>
                                        <p class="mt-4 text-lg text-gray-500">Owners, operators, mechanics, and other stakeholders including your financing and insurance companies, benefit from our streamlined services. Invite them with their own roles and permissions, and remove them at any time.</p>
                                        <div class="mt-6">
<!--                                            <a href="/sign-up" class="inline-flex rounded-lg bg-logix-light-blue px-4 py-1.5 text-base font-semibold leading-7 text-white shadow-sm ring-1 ring-indigo-600 hover:bg-logix-dark-blue hover:ring-logix-light-blue-700">Get started</a>-->
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-8 border-t border-gray-200 pt-6">
                                    <blockquote>
                                        <div>
                                            <p class="text-base text-gray-500">&ldquo;Wow, what a great job! I love the briefcase and the binder - they are first class and very classy! Count on me for a recommendation any time!&rdquo;</p>
                                        </div>
                                        <footer class="mt-3">
                                            <div class="flex items-center space-x-3">
                                                <div class="flex-shrink-0">
                                                    <img class="h-6 w-6 rounded-full" src="https://logixpublic.s3.amazonaws.com/partnerships/phimes.jpg" alt="" />
                                                </div>
                                                <div class="text-base font-medium text-gray-700">Paul Himes, Piper Meridian Owner</div>
                                            </div>
                                        </footer>
                                    </blockquote>
                                </div>
                            </div>
                            <div class="mt-12 sm:mt-16 lg:col-start-1 lg:mt-0">
                                <div class="-ml-48 pr-4 sm:pr-6 md:-ml-16 lg:relative lg:m-0 lg:h-full lg:px-0">
                                    <img class="w-full rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 lg:absolute lg:right-0 lg:h-full lg:w-auto lg:max-w-none" src="https://logixpublic.s3.amazonaws.com/features/users3.PNG" alt="Customer profile user interface" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-white py-24 sm:py-32">
                        <div class="mx-auto max-w-7xl px-6 lg:px-8">
                            <div class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                <div>
                                    <h2 class="text-base font-semibold leading-7 text-indigo-600">Everything you need</h2>
                                    <p class="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">All-in-one platform</p>
                                    <p class="mt-6 text-base leading-7 text-gray-600">From single engine piston operators to twin jet, our platform is designed to help you maintain compliance and enhance maintenance awareness.</p>
                                    <p class="mt-6 text-base leading-7 text-gray-600">We are, simply put, the gold standard in digital maintenance records for General Aviation. Amazingly, we are also priced appropriately for all types of owners and operators.</p><div class="mt-6">
<!--                                    <a href="/sign-up" class="inline-flex rounded-lg bg-logix-light-blue px-4 py-1.5 text-base font-semibold leading-7 text-white shadow-sm ring-1 ring-indigo-600 hover:bg-logix-dark-blue hover:ring-logix-light-blue-700">Get started</a>-->
                                </div>
                                </div>
                                <dl class="col-span-2 grid grid-cols-1 gap-x-8 gap-y-10 text-base leading-7 text-gray-600 sm:grid-cols-2 lg:gap-y-16">
                                    <div v-for="feature in features" :key="feature.name" class="relative pl-9" >
                                        <dt class="font-semibold text-gray-900" v-if="feature.show">
                                            <CheckIcon class="absolute left-0 top-1 h-5 w-5 text-indigo-500" aria-hidden="true" />
                                            {{ feature.name }}
                                        </dt>
                                        <dd class="mt-2" v-html="feature.description" v-if="feature.show"></dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>


    </LandingPageLayout>


</template>
